import { useCallback, useRef, useState } from 'react';
import { useUpload } from './useUpload';
import api from '../utils/api';

interface UseUploadImageOrderReturn {
  reorderAndPersist: (orderedImageIds: string[]) => Promise<void>;
  error: string | null;
}

/**
 * Hook for persisting drag-to-reorder of catalog images on the upload page.
 *
 * Uses optimistic updates with rollback on failure, mirroring
 * useUploadCoverImage and useUploadImageVisibility. When catalogId is null
 * (the user hasn't uploaded yet), only the local order is updated — no
 * API call. On rapid back-to-back drags the latest request wins on the
 * server (each PATCH carries the full ordered list); locally, we skip the
 * rollback when a newer drag has already superseded our optimistic update,
 * otherwise we'd erase the user's most recent intent.
 */
export function useUploadImageOrder(ownerId?: string | null): UseUploadImageOrderReturn {
  const { catalogId, uploadedImages, reorderImages } = useUpload();
  const [error, setError] = useState<string | null>(null);

  // Sentinel for the most recently fired optimistic order. Each invocation
  // stores the array reference it just optimistically applied; on failure,
  // a closure compares its own array reference to the sentinel — if a newer
  // drag has overwritten it, the sentinel will not match and we skip the
  // rollback (avoids clobbering a superseding drag's work).
  const lastOptimisticOrderRef = useRef<readonly string[] | null>(null);

  const reorderAndPersist = useCallback(
    async (orderedImageIds: string[]): Promise<void> => {
      const previousOrder = uploadedImages.map((img) => img.imageId);

      reorderImages(orderedImageIds);
      lastOptimisticOrderRef.current = orderedImageIds;
      setError(null);

      if (!catalogId) {
        return;
      }

      const url = ownerId
        ? `/api/catalogs/${catalogId}/images/order?owner_id=${ownerId}`
        : `/api/catalogs/${catalogId}/images/order`;

      try {
        await api.patch(url, { imageIds: orderedImageIds });
      } catch (err) {
        // Reference equality: only rollback if our optimistic order is still
        // the latest one applied. A newer drag would have overwritten the
        // sentinel with its own array reference.
        if (lastOptimisticOrderRef.current === orderedImageIds) {
          reorderImages(previousOrder);
        }
        const message = err instanceof Error ? err.message : 'Failed to update image order';
        setError(message);
      }
    },
    [catalogId, ownerId, reorderImages, uploadedImages],
  );

  return { reorderAndPersist, error };
}
